<template>
  <a-dialog
      ref="dialog"
      :persistent="false"
      size="large"
      name="manage_tags"
      @close="close">
    <span slot="title">
      {{ $t('title') }}
    </span>
    <div slot="content" class="mt-4">
      <a-action-buttons-bar
          class="mb-4"
          :options="{ selected: table.selected}"
          :buttons="buttons">
      </a-action-buttons-bar>

      <v-expand-transition>
        <v-form ref="form" v-model="create.valid" v-show="create.opened" class="mb-4">
          <v-row>
            <v-col cols="6" class="pb-0">
              <v-text-field
                  filled
                  :prepend-icon="$vuetify.breakpoint.smAndUp ? 'mdi-tag-text-outline' : ''"
                  v-model="create.fields.name"
                  :label="$t('tag-name-field')"
                  :hint="$t('tag-name-hint')"
                  persistent-hint
                  :rules="create.rules.name"
                  @keydown.enter.prevent="createTag">
              </v-text-field>
            </v-col>
            <v-col cols="6" class="pb-0">
              <v-text-field
                  filled
                  :prepend-icon="$vuetify.breakpoint.smAndUp ? 'mdi-format-color-fill' : ''"
                  v-model="create.fields.color"
                  v-mask="create.mask.color"
                  :label="$t('tag-color-field')"
                  :hint="$t('tag-color-hint')"
                  persistent-hint
                  :rules="create.rules.color"
                  @keydown.enter.prevent="createTag">
                <template v-slot:append>
                  <v-menu v-model="create.menu.color" top nudge-bottom="248" nudge-left="16"
                          :close-on-content-click="false">
                    <template v-slot:activator="{ on }">
                      <div :style="swatchStyle" v-on="on"/>
                    </template>
                    <v-card>
                      <v-card-text class="pa-0">
                        <v-color-picker v-model="create.fields.color" flat show-swatches/>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                  :disabled="!create.valid"
                  :loading="create.loading"
                  color="success"
                  @click="createTag"
              >
                {{ $t('button-create-action') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-expand-transition>

      <v-text-field
          class="mb-4"
          dense
          rounded
          filled
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          :label="$t('actions.search-three-dots')"
          single-line
          hide-details
          append-icon="mdi-close"
          @click:append="search = ''"
      ></v-text-field>

      <v-data-table
          dense
          must-sort
          :items="table.items.all"
          :options.sync="table.options"
          sort-by="name"
          :sort-desc="false"
          :server-items-length="table.items.length"
          :loading="table.items.loading"
          :headers="table.headers"
          :show-select="$vuetify.breakpoint.mdAndUp"
          v-model="table.selected"
          itemid="id"
      >
        <template v-slot:item.name="{ item }">
          <v-edit-dialog
              @open="dialog.value = item.name"
              @save="updateTag({...item, name: dialog.value})"
              large
              :cancel-text="$t('actions.cancel')"
              :save-text="$t('actions.save')">
            <span class="editable">{{ item.name }}</span>
            <edit-dialog-pencil></edit-dialog-pencil>
            <template v-slot:input>
              <v-text-field
                  v-model="dialog.value"
                  :rules="create.rules.name">
              </v-text-field>
            </template>
          </v-edit-dialog>
        </template>
        <template v-slot:item.color="{ item }">
          <v-edit-dialog
              @open="dialog.value = item.color"
              @save="updateTag({...item, color: dialog.value})"
              large
              :cancel-text="$t('actions.cancel')"
              :save-text="$t('actions.save')">
            <v-badge inline :color="item.color" dot></v-badge>
            <span class="editable">{{ item.color }}</span>
            <edit-dialog-pencil></edit-dialog-pencil>
            <template v-slot:input>
              <v-text-field
                  v-model="dialog.value"
                  v-mask="create.mask.color"
                  :rules="create.rules.color">
              </v-text-field>
            </template>
          </v-edit-dialog>
        </template>
      </v-data-table>
    </div>
  </a-dialog>
</template>

<script>
import EditDialogPencil from '@/views/Common/Components/Table/EditDialogPencil'
import { debounce } from 'lodash'
export default {
  name: 'ManageTagsDialog',
  components: { EditDialogPencil },
  data () {
    return {
      create: {
        opened: false,
        valid: false,
        loading: false,
        fields: {
          name: '',
          color: '#1976D2',
        },
        rules: {
          name: [
            v => !!v || this.$t('tag-name-required'),
          ],
          color: [
            v => !!v || this.$t('tag-color-required'),
            v => v.length === 7 || this.$t('tag-color-required'),
          ],
        },
        mask: {
          color: '!#XXXXXX',
        },
        menu: {
          color: false
        }
      },
      dialog: {
        value: null
      },
      search: '',
      table: {
        options: {},
        items: this.$repos.tags.createDatatableCollection(),
        selected: [],
        headers: [
          { text: this.$t('tag-name-column'), value: 'name' },
          { text: this.$t('tag-color-column'), value: 'color', width: '150px' }
        ],
      },
      buttons: [
        {
          color: 'success',
          text: this.$t('button-create'),
          icon: 'mdi-plus',
          click: () => this.create.opened = !this.create.opened,
        },
        {
          color: 'error',
          confirmation: true,
          text: ({ selected }) => this.$t('actions.remove') + ' (' + selected.length + ')',
          icon: 'mdi-delete',
          disabled: ({ selected }) => selected.length === 0,
          click: ({ selected }) => this.removeTags(selected.map(selected => selected.id)),
        }
      ]
    }
  },
  watch: {
    'table.options': {
      handler: async function () {
        await this.fetch()
      },
      deep: true
    },
    search: debounce(async function () {
      await this.fetch()
    }, 250)
  },
  computed: {
    swatchStyle () {
      return {
        backgroundColor: this.create.fields.color,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        marginTop: '-3px',
        borderRadius: this.create.menu.color ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out'
      }
    }
  },
  methods: {
    async fetch () {
      if (Object.keys(this.table.options).length > 0) {
        await this.table.items.fetch({
          ...this.table.options,
          search: this.search
        })
      }
    },
    close () {
      this.table.items.destroy()
      this.$reset()
    },
    handleTableSelect (ids) {
      this.table.selected = ids
    },
    async createTag () {
      try {
        this.create.loading = true
        this.table.selected = []

        await this.$repos.tags.create(this.create.fields)
        this.$ui.success(this.$t('success'))

        this.create.fields.name = ''
        this.create.fields.color = '#1976D2'

        this.$refs.form.resetValidation()
      } finally {
        this.create.loading = false
      }
    },
    async updateTag (fields) {
      await this.$repos.tags.update(fields)
      this.$ui.success(this.$t('success'))
    },
    async removeTags (ids) {
      this.table.selected = []
      await this.$repos.tags.remove(ids)
    }
  }
}
</script>

<i18n>
{
	"en": {
		"button-create": "Create",
		"button-create-action": "Create new tag",
		"tag-color-column": "Color",
		"tag-color-field": "Tag color",
		"tag-color-hint": "The color that will be displayed in the table.",
		"tag-color-required": "A valid color in RGB format (#XXXXXX) is required.",
		"tag-name-column": "Name",
		"tag-name-field": "Tag name",
		"tag-name-hint": "The name that will be used in the table and in the search engine.",
		"tag-name-required": "The tag name is required.",
		"title": "Manage my tags"
	},
	"es": {
		"button-create": "Crear",
		"button-create-action": "Crear nueva etiqueta",
		"tag-color-column": "Color",
		"tag-color-field": "Color de la etiqueta",
		"tag-color-hint": "El color que se mostrará en la tabla.",
		"tag-color-required": "Se requiere un color válido en formato RGB (#XXXXXX).",
		"tag-name-column": "Apellido",
		"tag-name-field": "Nombre de la etiqueta",
		"tag-name-hint": "El nombre que se utilizará en la tabla y en el motor de búsqueda.",
		"tag-name-required": "El nombre de la etiqueta es obligatorio.",
		"title": "Administrar mis etiquetas"
	},
	"fr": {
		"button-create": "Ajouter",
		"button-create-action": "Ajouter un nouveau tag",
		"tag-color-column": "Couleur",
		"tag-color-field": "Couleur du tag",
		"tag-color-hint": "La couleur qui sera affichée dans le tableau.",
		"tag-color-required": "Une couleur valide au format RVB (#XXXXXX) est requise.",
		"tag-name-column": "Nom",
		"tag-name-field": "Nom du tag",
		"tag-name-hint": "Le nom qui sera utilisé dans le tableau et dans le moteur de recherche.",
		"tag-name-required": "Le nom du tag est requis.",
		"title": "Gérer mes tags"
	},
	"it": {
		"button-create": "Aggiungi",
		"button-create-action": "Aggiungi nuovo tag",
		"tag-color-column": "Colore",
		"tag-color-field": "Colore del tag",
		"tag-color-hint": "Il colore che verrà visualizzato nella tabella.",
		"tag-color-required": "È richiesto un colore valido in formato RGB (#XXXXXX).",
		"tag-name-column": "Nome",
		"tag-name-field": "Nome del tag",
		"tag-name-hint": "Il nome che verrà usato nella tabella e nel motore di ricerca.",
		"tag-name-required": "Il nome del tag è obbligatorio.",
		"title": "Gestisci i miei tag"
	},
	"pt": {
		"button-create": "Criar",
		"button-create-action": "Criar nova tag",
		"tag-color-column": "Cor",
		"tag-color-field": "Cor da tag",
		"tag-color-hint": "A cor que será exibida na tabela.",
		"tag-color-required": "É necessária uma cor válida no formato RGB (#XXXXXX).",
		"tag-name-column": "Nome",
		"tag-name-field": "Nome da tag",
		"tag-name-hint": "O nome que será utilizado na tabela e no motor de pesquisa.",
		"tag-name-required": "O nome da tag é obrigatório.",
		"title": "Gerir as minhas tags"
	},
	"pl": {
		"button-create": "Utwórz",
		"button-create-action": "Utwórz nowy tag",
		"tag-color-column": "Kolor",
		"tag-color-field": "Kolor tagu",
		"tag-color-hint": "Kolor, który będzie wyświetlany w tabeli.",
		"tag-color-required": "Wymagany jest prawidłowy kolor w formacie RGB (#XXXXXX).",
		"tag-name-column": "Nazwa",
		"tag-name-field": "Nazwa tagu",
		"tag-name-hint": "Nazwa, która będzie używana w tabeli i w wyszukiwarce.",
		"tag-name-required": "Nazwa tagu jest wymagana.",
		"title": "Zarządzaj moimi tagami"
	}
}
</i18n>
